import type { ReactNode } from 'react'
import styles from './RangoRadioGroup.module.css'

type Option = { value: string; label: ReactNode }

type Props = {
  /** Vai para o primeiro radio: é o alvo do foco quando o grupo tem erro. */
  id: string
  name: string
  legend: ReactNode
  options: Option[]
  value: string
  onChange: (value: string) => void
  error?: ReactNode
  disabled?: boolean
}

/**
 * Radio group Rango DS — `fieldset` + `legend` (nome do grupo para leitor de
 * tela), radios nativos (setas do teclado de graça), área de toque mínima
 * `$size-6x` (48px) e erro inline associado ao grupo.
 */
export function RangoRadioGroup({
  id,
  name,
  legend,
  options,
  value,
  onChange,
  error,
  disabled = false,
}: Props) {
  const errorId = error ? `${id}-error` : undefined

  return (
    <fieldset
      className={styles.group}
      aria-invalid={error ? true : undefined}
      aria-describedby={errorId}
      disabled={disabled}
    >
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.options}>
        {options.map((option, index) => (
          <label key={option.value} className={styles.option}>
            <input
              id={index === 0 ? id : `${id}-${option.value}`}
              type="radio"
              name={name}
              value={option.value}
              className={styles.input}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className={styles.circle} aria-hidden="true" />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}
